<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('customer.title') }}</span>
          <div class="header-actions-wrapper">
            <div class="header-actions">
              <el-button 
                type="primary" 
                @click="handleAdd"
                :icon="User"
              >
                {{ $t('customer.form.add') }}
              </el-button>
              <el-button 
                @click="handleRefresh"
                :icon="RefreshRight"
              >
                {{ $t('common.refresh') }}
              </el-button>
              <el-button 
                @click="phoneMaskEnabled = !phoneMaskEnabled"
                :icon="phoneMaskEnabled ? Hide : View"
                :type="phoneMaskEnabled ? 'warning' : 'info'"
                plain
                :title="phoneMaskEnabled ? $t('customer.maskPhone') : $t('customer.showPhone')"
              >
                {{ phoneMaskEnabled ? $t('customer.masked') : $t('customer.visible') }}
              </el-button>
              <el-dropdown @command="handleExportCommand" trigger="click">
                <el-button type="success">
                  <el-icon><Download /></el-icon>
                  {{ $t('common.export') }}
                  <el-icon class="el-icon--right"><ArrowDown /></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="xlsx">
                      <el-icon><Document /></el-icon>
                      Export as Excel (.xlsx)
                    </el-dropdown-item>
                    <el-dropdown-item command="csv">
                      <el-icon><Tickets /></el-icon>
                      Export as CSV (.csv)
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </div>
      </template>

      <div class="search-bar">
        <div class="search-filters">
          <el-input 
            v-model="searchQuery" 
            :placeholder="$t('customer.searchPlaceholder')" 
            clearable
            prefix-icon="Search"
            style="flex: 1; max-width: 400px;"
          />
          <el-select v-model="statusFilter" :placeholder="$t('customer.allStatus')" style="width: 150px; margin-left: 12px">
            <el-option :label="$t('customer.allStatus')" value="" />
            <el-option :label="$t('customer.statusOptions.active')" :value="1" />
            <el-option :label="$t('customer.statusOptions.inactive')" :value="0" />
            <el-option :label="$t('customer.statusOptions.deleted')" :value="2" />
            <el-option :label="$t('customer.statusOptions.blocked')" :value="3" />
          </el-select>
          <div class="sort-wrapper" style="margin-left: 12px;">
            <el-select v-model="sortBy" :placeholder="$t('customer.sortBy')" style="width: 120px;">
              <el-option :label="$t('customer.sortByCreated')" value="createdAt" />
            </el-select>
            <el-button 
              @click="toggleSortOrder" 
              :icon="sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'"
              :title="sortOrder === 'asc' ? $t('customer.sortAsc') : $t('customer.sortDesc')"
              class="sort-toggle-btn"
            />
          </div>
        </div>
        
        <div class="view-toggle">
          <el-button-group>
            <el-button 
              :type="viewMode === 'list' ? 'primary' : ''" 
              @click="viewMode = 'list'"
            >
              <el-icon><List /></el-icon>
            </el-button>
            <el-button 
              :type="viewMode === 'card' ? 'primary' : ''" 
              @click="viewMode = 'card'"
            >
              <el-icon><Grid /></el-icon>
            </el-button>
          </el-button-group>
        </div>
      </div>

      <!-- List View -->
      <el-table 
        v-if="viewMode === 'list'" 
        :data="filteredCustomers" 
        v-loading="loading" 
        stripe
      >
        <el-table-column type="index" width="50" />
        <el-table-column prop="avatar" :label="$t('customer.avatar')" width="100">
          <template #default="{ row }">
            <ImagePreview 
              v-if="row.avatar" 
              :src="row.avatar" 
              width="50px" 
              height="50px"
              :round="true"
              :enable-preview="true"
            />
            <i v-else class="iconfont icon-helmet avatar-icon"></i>
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="$t('customer.name')">
          <template #default="{ row }">
            <span>{{ row.name }}</span>
            <el-tooltip v-if="row.serviceAccountOpenId" :content="$t('customer.subscribed')" placement="top" effect="light">
              <el-icon class="subscribed-icon" style="margin-left: 6px; color: #67c23a; font-size: 16px;"><Bell /></el-icon>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="phone" :label="$t('customer.phone')">
          <template #default="{ row }">
            {{ maskPhone(row.phone) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('customer.gender')" width="80" align="center">
          <template #default="{ row }">
            {{ row.gender ? $t('customer.male') : $t('customer.female') }}
          </template>
        </el-table-column>
        <el-table-column prop="age" :label="$t('customer.age')" width="70" align="center">
          <template #default="{ row }">
            {{ row.age || '-' }}
          </template>
        </el-table-column>
        <el-table-column v-if="filteredCustomers.some(c => c.remark)" prop="remark" :label="$t('customer.remark')" min-width="150">
          <template #default="{ row }">
            <span class="remark-cell">{{ row.remark || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" :label="$t('customer.status')">
          <template #default="{ row }">
            <el-tag :type="getStatusTagType(row.status)">
              {{ getStatusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.actions')" width="280" fixed="right" align="center">
          <template #default="{ row }">
            <div class="action-buttons">
              <el-button type="primary" link @click="viewDetail(row)">
                {{ $t('common.edit') }}
              </el-button>
              <el-button type="warning" link @click="viewVipDetail(row)" v-if="row.status === 1">
                {{ $t('customer.vipInfo') }}
              </el-button>
              <el-button type="danger" link @click="handleDelete(row)">
                {{ $t('common.delete') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- Card View -->
      <div v-else class="customer-cards" v-loading="loading">
        <div v-if="filteredCustomers.length === 0" class="empty-state">
          <el-empty :description="$t('common.noData')" />
        </div>
        <div v-else class="cards-grid">
          <div 
            v-for="customer in filteredCustomers" 
            :key="customer.id" 
            class="customer-card"
          >
            <div class="card-header-section">
              <div class="customer-avatar" @click="previewAvatar(customer.avatar)">
                <img v-if="customer.avatar" :src="customer.avatar" alt="Avatar" class="avatar-image" />
                <i v-else class="iconfont icon-helmet avatar-icon"></i>
              </div>
              <div class="customer-info">
                <h3 class="customer-name">
                  {{ customer.name }}
                  <el-icon v-if="customer.gender" class="gender-icon-inline" title="Male"><Male /></el-icon>
                  <el-icon v-else class="gender-icon-inline" title="Female"><Female /></el-icon>
                  <el-tooltip v-if="customer.serviceAccountOpenId" :content="$t('customer.subscribed')" placement="top" effect="light">
                    <el-icon class="subscribed-icon-inline"><Bell /></el-icon>
                  </el-tooltip>
                  <span v-if="customer.age" class="customer-age-badge">{{ customer.age }} {{ $t('customer.ageUnit') }}</span>
                </h3>
                <a :href="`tel:${customer.phone}`" class="customer-phone">
                  <el-icon><Phone /></el-icon>
                  <span>{{ maskPhone(customer.phone) }}</span>
                </a>
              </div>
              <div class="status-badge" :class="getStatusClass(customer.status)">
                <el-icon v-if="customer.status === 1"><CircleCheck /></el-icon>
                <el-icon v-else><InfoFilled /></el-icon>
                <span>{{ getStatusLabel(customer.status) }}</span>
              </div>
            </div>

            <div class="card-body-section">
              <div v-if="customer.remark" class="remark-row">
                <el-icon class="remark-icon"><Tickets /></el-icon>
                <span class="remark-text">{{ customer.remark }}</span>
              </div>
            </div>
            
            <div class="card-footer-section">
              <el-button type="primary" size="default" plain @click="viewDetail(customer)">
                <el-icon><Edit /></el-icon>
                {{ $t('common.edit') }}
              </el-button>
              <el-button 
                v-if="customer.status === 1" 
                type="warning" 
                size="default" 
                plain
                @click="viewVipDetail(customer)"
              >
                <el-icon><Star /></el-icon>
                {{ $t('customer.vipInfo') }}
              </el-button>
              <el-button type="danger" size="default" plain @click="handleDelete(customer)">
                <el-icon><Delete /></el-icon>
                {{ $t('common.delete') }}
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- Image Preview Dialog -->
    <el-dialog v-model="showImagePreview" :title="$t('customer.avatarPreview')" width="500px" center>
      <div style="text-align: center;">
        <img :src="previewImageUrl" alt="Avatar Preview" style="max-width: 100%; max-height: 80vh; border-radius: 8px;" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox, ElImageViewer } from 'element-plus'
import { Search, RefreshRight, List, Grid, Download, ArrowDown, Document, Tickets, Phone, User, Edit, Delete, Star, CircleCheck, InfoFilled, Male, Female, Hide, View, Bell } from '@element-plus/icons-vue'
import { useCustomerStore } from '@/stores/customer'
import { exportToExcel, exportToCSV } from '@/utils/export'
import { imageCacheUtil } from '@/utils/image-cache'
import { usePhoneMask } from '@/composables/usePhoneMask'

const router = useRouter()
const { t } = useI18n()
const customerStore = useCustomerStore()
const { phoneMaskEnabled, maskPhone } = usePhoneMask()

const loading = ref(false)
const showImagePreview = ref(false)
const previewImageUrl = ref('')

const viewMode = ref(window.innerWidth < 768 ? 'card' : 'list')
const searchQuery = ref('')
const statusFilter = ref(1) // Default show active customers only
const sortBy = ref('createdAt') // Default sort by created time
const sortOrder = ref('desc') // Default sort descending (newest first)

const toggleSortOrder = () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
}

// Load saved view mode preference
onMounted(() => {
  const savedViewMode = localStorage.getItem('customerViewMode')
  if (savedViewMode) {
    viewMode.value = savedViewMode
  }
})

// Save view mode preference when it changes
watch(() => viewMode.value, (newValue) => {
  localStorage.setItem('customerViewMode', newValue)
})

// Computed property for real-time filtering and sorting
const filteredCustomers = computed(() => {
  const filtered = customerStore.customers.filter(customer => {
    // Status filter
    if (statusFilter.value !== '' && customer.status !== statusFilter.value) {
      return false
    }
    
    // Search query filter (name or phone)
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase()
      const matchName = customer.name?.toLowerCase().includes(query)
      const matchPhone = customer.phone?.includes(query)
      if (!matchName && !matchPhone) {
        return false
      }
    }
    
    return true
  })
  
  // Sort by creation time
  return [...filtered].sort((a, b) => {
    const result = new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
    return sortOrder.value === 'asc' ? result : -result
  })
})

const fetchData = async (forceRefresh = false) => {
  loading.value = true
  const params = {
    page: 1,
    pageSize: 1000 // Load all customers without pagination
  }
  const res = await customerStore.fetchCustomers(params, forceRefresh)
  if (res.success) {
    // Data loaded, filtering handled by computed property
    // VIP states will be loaded on-demand when viewing customer details
  }
  loading.value = false
}

// Handle manual refresh - clear cache and reload
const handleRefresh = async () => {
  console.log('[Customer List] Manual refresh...')
  
  // Clear cached image data
  imageCacheUtil.clearAllCachedImages()
  
  // Reload data from API
  await fetchData(true)
  
  ElMessage.success(t('customer.refreshSuccess'))
}



const viewDetail = (row) => {
  router.push(`/customer/detail/${row.id}`)
}

const handleAdd = () => {
  router.push('/customer/add')
}

const previewAvatar = (avatarUrl) => {
  if (!avatarUrl) return
  previewImageUrl.value = avatarUrl
  showImagePreview.value = true
}

const viewVipDetail = (row) => {
  router.push(`/customer/vip/${row.id}`)
}

const getStatusLabel = (status) => {
  const statusMap = {
    0: t('customer.statusOptions.inactive'),
    1: t('customer.statusOptions.active'),
    2: t('customer.statusOptions.deleted'),
    3: t('customer.statusOptions.blocked')
  }
  return statusMap[status] || 'Unknown'
}

const getStatusTagType = (status) => {
  const typeMap = {
    0: 'info',
    1: 'success',
    2: 'danger',
    3: 'warning'
  }
  return typeMap[status] || 'info'
}

const getStatusClass = (status) => {
  const classMap = {
    0: 'status-inactive',
    1: 'status-active',
    2: 'status-deleted',
    3: 'status-blocked'
  }
  return classMap[status] || 'status-inactive'
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      t('common.confirmDelete'),
      t('common.tip'),
      { type: 'warning' }
    )
    // Update status to deleted (2) instead of actual deletion
    await customerStore.modifyCustomer(row.id, { status: 2 })
    ElMessage.success(t('common.success'))
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error(error)
    }
  }
}

const handleExportCommand = (format) => {
  if (format === 'csv') {
    handleExport('csv')
  } else {
    handleExport('xlsx')
  }
}

const handleExport = (format = 'xlsx') => {
  try {
    const customers = filteredCustomers.value
    
    if (customers.length === 0) {
      ElMessage.warning(t('common.noData'))
      return
    }
    
    // Transform data for export
    const exportData = customers.map(customer => ({
      'ID': customer.id,
      'Name': customer.name || '',
      'Phone': customer.phone || '',
      'Gender': customer.gender ? t('customer.male') : t('customer.female'),
      'Status': getStatusLabel(customer.status),
      'Created At': customer.createTime ? new Date(customer.createTime).toLocaleString() : ''
    }))
    
    // Export based on format
    if (format === 'csv') {
      exportToCSV(exportData, 'customers')
    } else {
      exportToExcel(exportData, 'customers', 'Customers')
    }
  } catch (error) {
    console.error('Export failed:', error)
    ElMessage.error(error.message || t('common.exportFailed'))
  }
}

onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;
  flex-wrap: wrap;
}

.search-filters {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.view-toggle {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.header-actions-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.view-toggle {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

/* Table Avatar Icon */
.avatar-icon {
  font-size: 40px;
  color: #409eff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
}

/* Customer Cards */
.customer-cards {
  margin-top: 20px;
}

.customer-cards .empty-state {
  padding: 40px 0;
}

.customer-cards .cards-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 768px) {
  .customer-cards .cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1200px) {
  .customer-cards .cards-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.customer-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e4e7ed;
  overflow: hidden;
  transition: all 0.3s ease;
}

.customer-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
  border-color: #409eff;
}

.customer-card .card-header-section {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f5f7fa;
  position: relative;
  border-bottom: 1px solid #e4e7ed;
}

.customer-card .customer-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.customer-card .customer-avatar:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.customer-card .avatar-icon {
  font-size: 28px;
  color: #409eff;
}

.customer-card .icon-rider.avatar-icon {
  font-size: 30px;
  color: #409eff;
}

.customer-card .avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.customer-card .customer-info {
  flex: 1;
  min-width: 0;
}

.customer-card .customer-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
}

.customer-card .gender-icon-inline {
  font-size: 18px;
  flex-shrink: 0;
}

.customer-card .gender-icon-inline[title="Male"] {
  color: #409eff;
}

.customer-card .gender-icon-inline[title="Female"] {
  color: #f56c6c;
}

.customer-card .subscribed-icon-inline {
  font-size: 18px;
  color: #67c23a;
  flex-shrink: 0;
  margin-left: 6px;
}

.customer-card .customer-age-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: #909399;
  background: #f0f2f5;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 6px;
  flex-shrink: 0;
}

.customer-card .customer-phone {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #606266;
  font-size: 13px;
  text-decoration: none;
  transition: color 0.2s;
}

.customer-card .customer-phone:hover {
  color: #409eff;
}

.customer-card .customer-phone .el-icon {
  font-size: 14px;
}

.customer-card .status-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 16px;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  border: 1px solid #e4e7ed;
}

.customer-card .status-badge .el-icon {
  font-size: 14px;
}

.customer-card .status-badge.status-active {
  color: #67c23a;
  background: rgba(103, 194, 58, 0.1);
  border-color: rgba(103, 194, 58, 0.3);
}

.customer-card .status-badge.status-active .el-icon {
  color: #67c23a;
}

.customer-card .status-badge.status-inactive {
  color: #909399;
  background: rgba(144, 147, 153, 0.1);
  border-color: rgba(144, 147, 153, 0.3);
}

.customer-card .status-badge.status-inactive .el-icon {
  color: #909399;
}

.customer-card .status-badge.status-deleted {
  color: #f56c6c;
  background: rgba(245, 108, 108, 0.1);
  border-color: rgba(245, 108, 108, 0.3);
}

.customer-card .status-badge.status-deleted .el-icon {
  color: #f56c6c;
}

.customer-card .status-badge.status-blocked {
  color: #e6a23c;
  background: rgba(230, 162, 60, 0.1);
  border-color: rgba(230, 162, 60, 0.3);
}

.customer-card .status-badge.status-blocked .el-icon {
  color: #e6a23c;
}

.customer-card .customer-remark {
  padding: 6px 12px;
  background: #fafafa;
  border-radius: 8px;
  font-size: 12px;
  color: #606266;
  line-height: 1.4;
  word-break: break-word;
}

.customer-card .customer-remark .remark-label {
  font-weight: 500;
  color: #909399;
  flex-shrink: 0;
}

.customer-card .card-body-section {
  border-top: 1px solid #f0f2f5;
  padding: 12px 16px;
}

.remark-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.remark-row .remark-icon {
  font-size: 14px;
  color: #909399;
  flex-shrink: 0;
  margin-top: 2px;
}

.remark-row .remark-text {
  font-size: 12px;
  color: #606266;
  line-height: 1.4;
  word-break: break-word;
}

.remark-cell {
  color: #606266;
  font-size: 13px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

.customer-card .card-body-section {
  padding: 16px;
}

.customer-card .card-footer-section {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-top: 1px solid #e4e7ed;
}

.customer-card .card-footer-section .el-button {
  flex: 1;
  font-weight: 500;
}

/* Action buttons spacing in table */
.action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}


@media (max-width: 768px) {
  .page-container {
    padding: 8px;
  }

  .card-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .card-header > span {
    font-size: 15px;
  }

  .header-actions-wrapper {
    width: 100%;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .header-actions .el-button {
    flex-shrink: 0;
  }

  .search-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 16px;
  }

  .search-filters {
    flex-direction: column;
    width: 100%;
    gap: 10px;
  }

  .search-filters .el-input {
    width: 100% !important;
    max-width: 100% !important;
  }

  .search-filters .el-select {
    width: 100% !important;
    margin-left: 0 !important;
  }

  .sort-wrapper {
    margin-left: 0 !important;
    width: 100%;
    display: flex;
    gap: 8px;
  }

  .sort-wrapper .el-select {
    flex: 1;
  }

  .view-toggle {
    width: 100%;
    justify-content: center;
  }

  .view-toggle .el-button-group {
    width: 100%;
    display: flex;
  }

  .view-toggle .el-button-group .el-button {
    flex: 1;
  }

  .card-grid {
    grid-template-columns: 1fr;
  }
  
  .customer-cards .cards-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .customer-card .card-header-section {
    padding: 12px;
    gap: 10px;
  }
  
  .customer-card .customer-avatar {
    width: 48px;
    height: 48px;
  }
  
  .customer-card .avatar-icon {
    font-size: 24px;
  }
  
  .customer-card .icon-helmet.avatar-icon {
    font-size: 26px;
  }
  
  .customer-card .customer-name {
    font-size: 14px;
  }
  
  .customer-card .customer-phone {
    font-size: 12px;
  }
  
  .customer-card .status-badge {
    padding: 4px 8px;
    font-size: 11px;
  }
  
  .customer-card .card-body-section {
    padding: 12px;
  }
  
  .customer-card .info-label {
    font-size: 12px;
  }
  
  .customer-card .info-value {
    font-size: 12px;
  }
  
  .customer-card .card-footer-section {
    padding: 10px 12px;
    gap: 6px;
  }
  
  .customer-card .card-footer-section .el-button {
    font-size: 12px;
    padding: 8px 12px;
  }
}
</style>